import GamedayApiService from "../gameday-api-service";
import NotImplemented from "../../exceptions/not-implemented";
import {
    contextRepository,
    eventRepository,
    gameRepository,
    playerRepository,
    streamRepository,
    syncRepository,
    teamRepository
} from "../../storage/storage";
import EventService from "../event-service";
import GameService from "../game-service";
import { EventStatusCode } from "../../sports/common/event-status";
import Sync from "../../sports/common/sync";
import ContextService from "../context-service";
import { isObject, isString } from "../common/object-util";
import { sanitizeInvalidCharactersDeep } from "../common/string-util";
import StreamService from "../stream-service";
import TeamPlayer from "../../sports/common/players/TeamPlayer";
import NoPlayer from "../../sports/common/players/NoPlayer";
import ClockFactory from "../../sports/common/utils/clock-factory";
import { migrateClockConfig } from "../../sports/football/utils/field-config-utils";

export class SyncService {
    /**
     * @param {*} o
     * @param {{[p:string]:string}} updateMap
     * @returns {*}
     * @private
     */
    _updateProps(o, updateMap) {
        if (Array.isArray(o)) {
            return o.map((x) => this._updateProps(x, updateMap));
        } else if (isObject(o)) {
            let retArr = {};
            Object.keys(o).forEach((key) => {
                const updatedKey = updateMap[key] || key;
                retArr[updatedKey] = this._updateProps(o[key], updateMap);
            });
            return retArr;
        } else {
            return isString(o) ? updateMap?.[o] ?? o : o;
        }
    }

    async deleteSync(id) {
        return syncRepository.delete(id);
    }

    async getSync(id) {
        return syncRepository.getById(id);
    }

    async getSyncByEventId(eventId) {
        return syncRepository.where({ eventId: eventId }).first();
    }

    async saveSync(sync) {
        sync.id = await syncRepository.put(sync);
        return this.getSync(sync.id);
    }

    async getOrCreateSync(eventId) {
        const localSync = await this.getSyncByEventId(eventId);
        return localSync || new Sync(eventId);
    }

    async _overWriteSync(eventId, data) {
        const sync = await this.getOrCreateSync(eventId);
        sync.timeSynced = data.timeSynced;
        sync.queuedSync = {
            data: JSON.stringify(data),
            timestamp: sync.timeSynced
        };
        return this.saveSync(sync);
    }

    async updateQueuedSync(eventId, data) {
        const sync = await this.getOrCreateSync(eventId);
        return this.saveSync(
            Object.assign(sync, {
                queuedSync: {
                    data: JSON.stringify(data),
                    timestamp: data.timeSynced
                }
            })
        );
    }

    async performQueuedSync(eventId) {
        const sync = await this.getOrCreateSync(eventId);
        const { queuedSync } = sync;

        if (queuedSync && queuedSync.data) {
            try {
                const eventData = sanitizeInvalidCharactersDeep(
                    JSON.parse(queuedSync.data)
                );
                const { event, game } = eventData;
                const response = await GamedayApiService.saveEventData(
                    event.eventId,
                    eventData
                );
                if (game) {
                    await this._performSecondarySync(game);
                }
                Object.assign(sync, {
                    timeSynced: response
                        ? eventData.timeSynced
                        : sync.timeSynced,
                    syncError: !response
                });
            } catch (e) {
                console.error("Failed to sync");
                sync.syncError = true;
            } finally {
                await this.saveSync(sync);
            }
        }
    }

    async fetchEventData(externalEventId) {
        try {
            return JSON.parse(
                (await GamedayApiService.fetchEventData(externalEventId))
                    .eventData
            );
        } catch (error) {
            console.error("Unable to fetch external event data", error);
        }
    }

    async resetEventData(eventId) {
        try {
            let event = await EventService.setEventStatus(
                eventId,
                EventStatusCode.NOT_STARTED
            );
            event.score.home = null;
            event.score.away = null;
            event.gameConfiguration = null;
            await Promise.all([
                GamedayApiService.saveOrUpdateEvent(event),
                GamedayApiService.saveEventData(event.eventId, {})
            ]);

            const game = await GameService.getGameByEventId(eventId);
            if (game) {
                await GameService.deleteGame(game.id);
            }

            const gameContext = await ContextService.getGameContext(game.id);
            if (gameContext) {
                await ContextService.deleteContext(gameContext.id);
            }

            await StreamService.deleteStreams(eventId);

            const sync = await this.getSyncByEventId(eventId);
            if (sync) {
                await this.deleteSync(sync.id);
            }
        } catch (error) {
            console.error("Unable to reset event data", error);
        }
    }

    async getEventDataChanged(eventId) {
        const event = await eventRepository.getById(eventId);
        if (event) {
            const { timeSynced: lastTimeSynced } =
                (await this.getSyncByEventId(event.id)) || {};
            const remoteEventData = await this.fetchEventData(event.eventId);

            return {
                event,
                changed:
                    remoteEventData?.timeSynced &&
                    lastTimeSynced !== remoteEventData.timeSynced
            };
        }

        return {};
    }

    /**
     * @param {{[p:string]: *}} remoteAwayTeam
     * @param {{[p:string]: *}} remoteHomeTeam
     * @param {Team} mergedAwayTeam
     * @param {Team} mergedHomeTeam
     * @returns {{[p:string]: string}} A remote team id/teamId map pointing to the merged version's id/teamId.
     * @private
     */
    _getTeamIdMap(
        remoteAwayTeam,
        remoteHomeTeam,
        mergedAwayTeam,
        mergedHomeTeam
    ) {
        return {
            [remoteAwayTeam.id]: mergedAwayTeam.id,
            [remoteHomeTeam.id]: mergedHomeTeam.id,

            // External teamId does not change
            [remoteAwayTeam.teamId]: remoteAwayTeam.teamId,
            [remoteHomeTeam.teamId]: remoteHomeTeam.teamId
        };
    }

    /**
     * @param {string} localAwayTeamId
     * @param {string} localHomeTeamId
     * @param {{[p: string]: *}[]} remotePlayers
     * @param {{[p:string]: string}} teamIdMap Used to alternatively identify player based on uniform + teamId
     * @returns {Promise<{[p:string]: string}>} A remote player id map that point to an existing player id, or the remote id if the player only exists remotely.
     * @private
     */
    async _getPlayerIdMap(
        localAwayTeamId,
        localHomeTeamId,
        remotePlayers,
        teamIdMap
    ) {
        let updatedPlayerIds = {};
        const existingPlayers = [
            ...(await playerRepository
                .where({ teamId: localAwayTeamId })
                .toArray()),
            ...(await playerRepository
                .where({ teamId: localHomeTeamId })
                .toArray())
        ];

        remotePlayers.forEach((p) => {
            const foundIndex = existingPlayers.findIndex((ep) => {
                const playerIdMatch = ep.playerId && ep.playerId === p.playerId;
                const noPlayerId = !ep.playerId && !p.playerId;
                const teamAndUniformMatch =
                    ep.teamId === teamIdMap[p.teamId] &&
                    ep.uniform === p.uniform;

                // Only NA and TM Players should match with the second condition
                return playerIdMatch || (noPlayerId && teamAndUniformMatch);
            });
            updatedPlayerIds[p.id] =
                foundIndex >= 0 ? existingPlayers[foundIndex].id : p.id;
        });

        return updatedPlayerIds;
    }

    /**
     * Returns local players for both teams that are no longer present in the
     * incoming remote roster, flagged as logically deleted so they can be
     * persisted. Special players (TM/NA) and locally-added players without a
     * playerId are never removed.
     * @param {string} localAwayTeamId
     * @param {string} localHomeTeamId
     * @param {{[p:string]: string}} updatedPlayerIds Map of remote player id -> matched local id (or remote id when unmatched)
     * @returns {Promise<Player[]>}
     * @private
     */
    async _getRemovedLocalPlayers(
        localAwayTeamId,
        localHomeTeamId,
        updatedPlayerIds
    ) {
        const existingPlayers = [
            ...(await playerRepository
                .where({ teamId: localAwayTeamId })
                .toArray()),
            ...(await playerRepository
                .where({ teamId: localHomeTeamId })
                .toArray())
        ];
        const specialUniforms = [
            TeamPlayer.getUniform(),
            NoPlayer.getUniform()
        ];
        const matchedLocalIds = new Set(Object.values(updatedPlayerIds));

        return existingPlayers
            .filter(
                (p) =>
                    !specialUniforms.includes(p.uniform) &&
                    p.playerId &&
                    !matchedLocalIds.has(p.id)
            )
            .map((p) => {
                p.markDeleted();
                return p;
            });
    }

    /**
     * @param {{[p: string]: *}[]} remotePlayers
     * @param {{[p: string]: string}} updateMap
     * @returns {Player[]}
     * @private
     */
    _getMergedPlayers(remotePlayers, updateMap) {
        return this._updateProps(
            remotePlayers.filter((p) => p.playerId || updateMap[p.id]),
            updateMap
        ).map((player) => playerRepository.mapToEntity(player));
    }

    /**
     * @param {{[p: string]: *}[]} remoteStreams
     * @param {{[p: string]: string}} updateMap
     * @returns {Stream[]}
     * @private
     */
    _getMergedStreams(remoteStreams, updateMap) {
        return this._updateProps(remoteStreams, updateMap).map((stream) =>
            streamRepository.mapToEntity(stream)
        );
    }

    /**
     * @param {{[p:string]: *}} remoteTeam
     * @param {Team | null | undefined} existingTeam
     * @returns {Team}
     * @private
     */
    _getMergedTeam(remoteTeam, existingTeam) {
        return Object.assign(teamRepository.mapToEntity(remoteTeam), {
            id: existingTeam ? existingTeam.id : remoteTeam.id
        });
    }

    /**
     * @param {{[p:string]: *}} remoteGame
     * @param {string} existingEventId
     * @returns {FootballGame | FlagFootballGame | TennisGame}
     * @private
     */
    _getMergedGame(remoteGame, existingEventId) {
        return Object.assign(gameRepository.mapToEntity(remoteGame), {
            eventId: existingEventId
        });
    }

    /**
     * Map a remote Event, assign it an existing eventId and update the inner teamIds using the updatedTeamIds map
     * @param {{[p: string]: *}} remoteEvent
     * @param {string} existingEventId
     * @param {{[p:string]: string}} updateMap
     * @returns {Event}
     * @private
     */
    _getMergedEvent(remoteEvent, existingEventId, updateMap) {
        const mergedEvent = this._updateProps(remoteEvent, updateMap);
        return eventRepository.mapToEntity({
            ...mergedEvent,
            id: existingEventId
        });
    }

    /**
     * @param {{[p:string]: *}} remoteEvent
     * @param {{[p:string]: *}} remoteAwayTeam
     * @param {{[p:string]: *}} remoteHomeTeam
     * @param {{[p:string]: *}[]} remotePlayers
     * @param {{[p:string]: *}} remoteGameContext
     * @param {{[p:string]: *}} remoteGame
     * @param {{[p:string]: *}[]} remoteStreams
     * @returns {Promise<{mergedPlayers: Player[], updateMap: {[p: string]: string}, mergedEvent: Event, mergedGame: FootballGame | FlagFootballGame | TennisGame, mergedAwayTeam: Team, mergedContext: FootballGameContext | FlagFootballGameContext | TennisGameContext, mergedHomeTeam: Team, mergedStreams: Stream[]}>}
     * @private
     */
    async _mergeEvent(
        remoteEvent,
        remoteAwayTeam,
        remoteHomeTeam,
        remotePlayers,
        remoteGameContext,
        remoteGame,
        remoteStreams = []
    ) {
        const localEvent = await eventRepository
            .where({ eventId: remoteEvent.eventId })
            .first();

        const localAwayTeam = await teamRepository
            .where({
                teamId: remoteAwayTeam.teamId
            })
            .first();
        const localHomeTeam = await teamRepository
            .where({
                teamId: remoteHomeTeam.teamId
            })
            .first();

        const mergedAwayTeam = this._getMergedTeam(
            remoteAwayTeam,
            localAwayTeam
        );
        const mergedHomeTeam = this._getMergedTeam(
            remoteHomeTeam,
            localHomeTeam
        );
        const updatedTeamIds = this._getTeamIdMap(
            remoteAwayTeam,
            remoteHomeTeam,
            mergedAwayTeam,
            mergedHomeTeam
        );
        const updatedPlayerIds = await this._getPlayerIdMap(
            localAwayTeam.id,
            localHomeTeam.id,
            remotePlayers,
            updatedTeamIds
        );

        let updateMap = {
            ...updatedTeamIds,
            ...updatedPlayerIds
        };

        const mergedEvent = this._getMergedEvent(
            remoteEvent,
            localEvent.id,
            updateMap
        );

        const mergedGame = this._getMergedGame(remoteGame, localEvent.id);

        updateMap = this._getFinalUpdateMap(
            remoteEvent,
            remoteGame,
            mergedEvent,
            mergedGame,
            updateMap
        );

        const mergedContext = this._getMergedContext(
            remoteGameContext,
            updateMap
        );

        // Remote blobs can be legacy-shaped (clock settings still in
        // general) or drifted (context clockType disagreeing with config) —
        // e.g. pushed by devices on older app versions. Migrate the configs
        // and re-derive the clock from the context's own config so drift
        // never lands locally. Tennis no-ops inside the reconciler.
        if (mergedEvent.gameConfiguration) {
            mergedEvent.gameConfiguration = migrateClockConfig(
                mergedEvent.gameConfiguration
            );
        }
        if (mergedContext?.gameConfig) {
            mergedContext.gameConfig = migrateClockConfig(
                mergedContext.gameConfig
            );
        }
        if (mergedContext?.gameClock) {
            ClockFactory.applyClockConfig(
                mergedContext,
                mergedContext.gameConfig
            );
        }

        const mergedPlayers = this._getMergedPlayers(remotePlayers, updateMap);

        const removedPlayers = await this._getRemovedLocalPlayers(
            localAwayTeam.id,
            localHomeTeam.id,
            updatedPlayerIds
        );
        mergedPlayers.push(...removedPlayers);

        const mergedStreams = this._getMergedStreams(remoteStreams, updateMap);

        return {
            mergedAwayTeam,
            mergedHomeTeam,
            updateMap,
            mergedContext,
            mergedPlayers,
            mergedEvent,
            mergedGame,
            mergedStreams
        };
    }

    async collectEventData() {
        throw NotImplemented();
    }

    async overWriteEventData(eventId) {
        throw NotImplemented();
    }

    /**
     * @param {{[p:string]: *}} remoteContext
     * @param {{[p:string]: string}} updateMap
     * @returns {FootballGameContext | FlagFootballGameContext | TennisGameContext}
     * * @private
     */
    _getMergedContext(remoteContext, updateMap) {
        return contextRepository.mapToEntity(
            this._updateProps(remoteContext, updateMap)
        );
    }

    /**
     * @param {{[p:string]: *}} remoteEvent
     * @param {{[p:string]: *}} remoteGame
     * @param {Event} mergedEvent
     * @param {FootballGame | FlagFootballGame | TennisGame} mergedGame
     * @param {{[p:string]: string}} updateMap
     * @returns {{[p:string]:string}}
     * @private
     */
    _getFinalUpdateMap(
        remoteEvent,
        remoteGame,
        mergedEvent,
        mergedGame,
        updateMap
    ) {
        return {
            ...updateMap,
            [remoteEvent.id]: mergedEvent.id,
            [remoteGame.id]: mergedGame.id
        };
    }

    async _performSecondarySync() {
        throw NotImplemented();
    }
}

export default new SyncService();
